import { Box, Button, Chip, Drawer, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import ArrowRightOutlinedIcon from '@mui/icons-material/ArrowRightOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useCommentsByOrderQuery } from '../../../queries/__generated__/commentsByOrder.generated';
import useOpenable from '../../../hooks/useOpenable';
import { orderAtom } from '../../state/state';
import CommentCard from './CommentCard';
import AddComment from './AddComment';

export interface OrderCommentsProps {
  compact?: boolean;
}

export default function OrderComments({ compact }: OrderCommentsProps): React.JSX.Element {
  const { t } = useTranslation(['comments']);
  const { open, close, isOpen } = useOpenable();
  const { reference_id: orderReferenceId, id: orderId } = useAtomValue(orderAtom);
  const { data } = useCommentsByOrderQuery({
    variables: { orderReferenceId },
    fetchPolicy: 'network-only',
    skip: !orderReferenceId,
  });
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {compact && !!data && (
        <Chip
          variant="assistive"
          onClick={open}
          label={
            data.comment.length
              ? t('comments:comment_with_count', { count: data.comment.length })
              : t('comments:comment')
          }
          icon={<AddCommentOutlinedIcon />}
        />
      )}
      {!compact && !!data && (
        <ListItemButton onClick={open}>
          <ListItemIcon>
            <MessageOutlinedIcon />
          </ListItemIcon>
          <ListItemText
            primary={t('comments:N_comments', { count: data.comment.length })}
          />
          <ListItemIcon>
            <ArrowRightOutlinedIcon />
          </ListItemIcon>
        </ListItemButton>
      )}
      {!!data && (
        <Drawer
          anchor={isXs ? 'bottom' : 'right'}
          open={isOpen}
          onClose={close}
          slotProps={{
            paper: {
              sx: {
                boxSizing: 'border-box',
                backgroundColor: 'surfaceContainerLow.main',
                width: {
                  sm: 440,
                },
                px: 3,
                pt: 3,
                pb: 0,
                gap: 2,
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="headlineSmall">
              {t('comments:N_comments', { count: data.comment.length })}
            </Typography>
            <Button variant="text" onClick={close}>
              <CloseIcon />
            </Button>
          </Box>
          <Box sx={{ flex: 1 }}>
            {data.comment.length
              ? data.comment.map((comment) => (
                <CommentCard key={comment.id} comment={comment} />
              ))
              : (
                <Typography variant="bodyLarge" color="onSurfaceVariant">
                  {t('comments:share_something_about_this_order')}
                </Typography>
              )}
          </Box>
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'surfaceContainerLow.main',
              paddingBottom: 2,
            }}
          >
            <AddComment orderReferenceId={orderReferenceId} orderId={orderId} />
          </Box>
        </Drawer>
      )}
    </>
  );
}
